import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { LyfeTooltip, useDataQuery } from "@lyfegen-platform/react-common";
import { byDesc, getTreatmentCodeByCountry, sortByComparisons } from "@lyfegen-platform/utils";
import { Typography } from "@material-ui/core";
import { useFieldValue } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { GET_AGREEMENT_CASES } from "../../controllers/queries";
import { GetAgreementCases, GetAgreementCases_cases } from "../../controllers/types/GetAgreementCases";
import useQueryParams from "../../hooks/use-query-params";
import { BaseAgreement, ContractStatus, CounterpartyRole } from "../../models/generated/graphql";
import { flexibleFormRelatedAgreements, flexibleFormValuesState } from "../../store/flexible";
import { UserPersonalInfo, userPersonalInfoState } from "../../store/user";
import { CaseStatus, CoverageRequestTherapyDecision, NegotiationRoundStatus } from "../../types/graphql-global-types";
import { getAttributeValueDeep, getAttributeValuesDeep } from "../../utils/agreement-utils";
import { formatTreatment } from "../../utils/base-agreement-formatters";
import renderCurrency from "../../utils/currency";
import { maxBy } from "../../utils/max-by";
import LyfeSimpleTable, { TableColumnAlignmentType } from "../LyfeTable/LyfeSimpleTable";
import useStyles from "./styles";

export enum FlexTableType {
	PRICE_PREVIEW,
	RELATED_CASES,
}

interface FlexTableProps {
	flexTableType: FlexTableType;
}

export const mapAttributeToTable: { [key: string]: FlexTableType } = {
	pricePreview: FlexTableType.PRICE_PREVIEW,
	recentCases: FlexTableType.RELATED_CASES,
};

function getDiscountsRates(caseInstance: GetAgreementCases_cases) {
	const typedFlexEntities = caseInstance.flexEntitiesConnection.edges
		.map(({ node: entity }) => ({ ...entity, data: JSON.parse(entity.data) }))
		.sort(sortByComparisons(byDesc((entity) => entity.createdOn)));
	const typedNegotiationRounds = caseInstance.negotiationRounds
		.filter(({ status }) => status === NegotiationRoundStatus.ACCEPTED || status === NegotiationRoundStatus.ACCEPTED_WITHOUT_REVIEW)
		.map((entity) => ({
			...entity,
			data: JSON.parse(entity.data),
		}))
		.sort(sortByComparisons(byDesc(({ data }) => getAttributeValueDeep(data, AttributeIdentifiers.Timeframe)?.[0])));
	const negotiatedRates = typedNegotiationRounds
		.map((round) => getAttributeValuesDeep(round.data, AttributeIdentifiers.DiscountRate))
		.find((rates) => rates.length);
	const sourceRates = () =>
		typedFlexEntities
			.map((entity) => getAttributeValuesDeep(entity.data, AttributeIdentifiers.SuggestedDiscountRate))
			.find((rates) => rates.length);

	return (
		(negotiatedRates || sourceRates())
			?.filter(Boolean)
			.map((rate) => `${rate}%`)
			.join(" / ") || ""
	);
}

function useRelevantCases(
	agreements: Array<BaseAgreement> | null,
	formValues: { disease: { Icd10Code: string }; preparations: Array<{ brandName: { Name: string } }> },
	userData: UserPersonalInfo
) {
	const allAgreementVersions = agreements?.flatMap((agreement) => {
		if (!!agreement.initialVersion) {
			return [agreement.initialVersion as unknown as BaseAgreement].concat(...(agreement.initialVersion?.nextVersions || []));
		}

		if (!!agreement.nextVersions) {
			return [agreement as unknown as BaseAgreement].concat(...(agreement.nextVersions || []));
		}

		return [agreement];
	});

	const relevantAgreementIds = allAgreementVersions
		? allAgreementVersions
				.filter(
					(agreement) =>
						!agreement.isHidden &&
						agreement.status === ContractStatus.Running &&
						agreement.negotiableAttributes &&
						agreement.negotiableAttributes.some(
							(negotiableAttribute) => negotiableAttribute.identifier === AttributeIdentifiers.SuggestedDiscountRate
						) &&
						agreement.counterpartiesConnection.edges.some(
							(counterpartiesConnection) =>
								counterpartiesConnection.node.name === userData.organization.name &&
								[CounterpartyRole.Buyer, CounterpartyRole.Seller].includes(counterpartiesConnection.role)
						)
				)
				.map((agreement) => agreement.id)
		: [];

	const { data, loading } = useDataQuery<GetAgreementCases>(GET_AGREEMENT_CASES, {
		variables: {
			agreementIds: relevantAgreementIds,
		},
		skip: !relevantAgreementIds?.length,
	});

	return {
		cases: data?.cases.filter((caseEntity) => {
			const startingPoint = caseEntity.flexEntitiesConnection.edges.find(({ isStartingPoint }) => isStartingPoint)!.node;
			const parsedData = JSON.parse(startingPoint.data);
			const pastCaseStatus = caseEntity.history.map((historyRecord) => historyRecord.status);

			return (
				((caseEntity.status === CaseStatus.ACTIVE ||
					(caseEntity.status === CaseStatus.IN_REVIEW && pastCaseStatus.includes(CaseStatus.ACTIVE))) &&
					parsedData.disease.Icd10Code) === formValues.disease.Icd10Code &&
				formValues.preparations.every((preparation) =>
					parsedData.preparations
						.map((preparation: { brandName: { Name: string } }) => preparation.brandName.Name)
						.includes(preparation.brandName.Name)
				)
			);
		}),
		loading,
	};
}

const RelatedCasesFlexTable = () => {
	const agreementsData = useRecoilValue(flexibleFormRelatedAgreements);
	const formValues: { disease: { Icd10Code: string }; preparations: Array<{ brandName: { Name: string } }> } =
		useRecoilValue(flexibleFormValuesState);
	const userData = useRecoilValue(userPersonalInfoState);
	const [therapyDecision] = useFieldValue(AttributeIdentifiers.DecisionTherapy);
	const { t } = useTranslation();
	const caseId = useQueryParams().get("caseId");
	const { relatedCasesTable, tableBox } = useStyles();

	const tableClassName = relatedCasesTable;
	const { cases, loading } = useRelevantCases(agreementsData.data?.map(({ agreement }) => agreement) || null, formValues, userData);
	const relevantCases = cases?.filter((caseRow) => caseRow.id !== caseId);
	const rows = relevantCases
		?.map((caseEntity) => {
			const caseFlexEntities = caseEntity.flexEntitiesConnection.edges.filter(
				({ node: flexEntity }) => JSON.parse(flexEntity.data)[AttributeIdentifiers.DecisionTherapy] === therapyDecision
			);

			if (!caseFlexEntities.length) return null;

			return {
				createdOn: caseEntity.creationDate,
				caseId: (
					<Link to={`/cases/case?id=${caseEntity.id}`} target="_blank">
						<Typography variant="body2">{caseEntity.id}</Typography>
					</Link>
				),
				refund: getDiscountsRates(caseEntity),
				rating: maxBy(caseFlexEntities, (flexEntity) => flexEntity.node.createdOn).node.rating || "n/a",
			};
		})
		.filter(Boolean) as Array<{
		createdOn: any;
		caseId: JSX.Element;
		refund: string;
		rating: string;
	}>;

	const columns = [
		{ headerName: "createdOn", key: "createdOn", alignment: TableColumnAlignmentType.LEFT },
		{ headerName: "caseId", key: "caseId", alignment: TableColumnAlignmentType.LEFT, tdStyle: { color: "#367bf5" } },
		{ headerName: "refund", key: "refund", alignment: TableColumnAlignmentType.RIGHT },
		{ headerName: "rating", key: "rating", alignment: TableColumnAlignmentType.RIGHT },
		{ headerName: "  ", key: "", alignment: TableColumnAlignmentType.RIGHT },
	];

	const shouldShowRelatedCases =
		!loading && !!relevantCases && !!therapyDecision && therapyDecision !== CoverageRequestTherapyDecision.DECLINED;

	if (!shouldShowRelatedCases) {
		return <></>;
	}

	return (
		<>
			<Typography style={{ marginLeft: 8 }}>{t("related-cases-message")}</Typography>
			<LyfeSimpleTable
				rows={rows || []}
				columns={columns || []}
				className={tableClassName}
				alternatingBackgroundColor={"#edf2f3"}
				boxClassName={tableBox}
			/>
		</>
	);
};

const PriceReviewFlexTable = () => {
	const agreementsData = useRecoilValue(flexibleFormRelatedAgreements);
	const { pricePreviewTable } = useStyles();
	const tableClassName = pricePreviewTable;
	const mostRecentAgreement = maxBy(agreementsData.data || [], ({ agreement }) => agreement.pushedOn)?.agreement;

	const rows = mostRecentAgreement
		? mostRecentAgreement.treatmentPricesConnection.edges.map((treatmentPriceEdge) => ({
				price: (
					<LyfeTooltip placement="right-end" title={mostRecentAgreement.currency?.Name || ""}>
						<div>{renderCurrency(treatmentPriceEdge.price, mostRecentAgreement.currency?.Code, 2, 2)}</div>
					</LyfeTooltip>
				),
				productName: formatTreatment(treatmentPriceEdge.node),
				productCode: getTreatmentCodeByCountry(treatmentPriceEdge.node),
		  }))
		: [];

	const columns = [
		{ headerName: "treatmentName", key: "productName", alignment: TableColumnAlignmentType.LEFT },
		{ headerName: "productCode", key: "productCode", alignment: TableColumnAlignmentType.LEFT },
		{ headerName: "base-price", key: "price", alignment: TableColumnAlignmentType.LEFT },
	];

	return <LyfeSimpleTable rows={rows || []} columns={columns || []} className={tableClassName} />;
};

export default function FlexTableSelector(props: FlexTableProps) {
	return props.flexTableType === FlexTableType.RELATED_CASES ? <RelatedCasesFlexTable /> : <PriceReviewFlexTable />;
}
